"use client";

import React from "react";
import { Box, Stack, SxProps, Theme } from "@mui/material";
import { deepmerge } from "@mui/utils";
import { VideoData } from "@/types/dto/common/video";
import FlexibleCardModuleSkeleton from "@/components/careers-page/flexible-card/flexible-card-module.skeleton";
import { BasicInfoCta } from "@/types/dto/common/basic-info";
import { MediaCard } from "@/types/dto/common/media-card-section";
import {
  isDarkLandingPageBackground,
  resolveLandingPageAnchorTextColor,
  resolveLandingPageSectionBackgroundColor
} from "@/utils/landing-page-background";
import MediaSection, { MediaCardSlotProps } from "./media-card";
import DefaultText from "./default-text";
import bgColorMap from "./constants";

export interface MediaTextCardTextComponentProps {
  headerText?: string;
  copyText?: string;
  ctas?: BasicInfoCta[];
  anchorTextColor?: string;
  slotProps?: MediaTextCardTextSlotProps;
  testId: string | number;
}

export interface MediaTextCardTextSlotProps {
  root?: {
    sx?: SxProps<Theme>;
  };
  fullWidthContainer?: {
    sx?: SxProps<Theme>;
  };
  container?: {
    sx?: SxProps<Theme>;
  };
  header?: {
    sx?: SxProps<Theme>;
  };
  copy?: {
    sx?: SxProps<Theme>;
  };
  textContainer?: {
    sx?: SxProps<Theme>;
  };
  media?: MediaCardSlotProps
}

export interface MediaTextCardProps {
  testId: string | number;
  card?: MediaCard;
  reverseDesktop?: boolean;
  reverseMobile?: boolean;
  index?: number;
  background?: string;
  sectionBackgroundColor?: string;
  isLoading?: boolean;
  bgImage?: string;
  showImageText?: boolean;
  useCircularPlayButton?: boolean;
  ctas?: BasicInfoCta[];
  dataParentContainerType?: string;
  moduleRankingPosition?: number;
  dataModuleName?: string;
  children?: React.ReactNode;
  slotProps?: MediaTextCardTextSlotProps;
}

const MediaTextCard: React.FC<MediaTextCardProps> = ({
  testId = "media-text-card",
  card,
  reverseDesktop = false,
  reverseMobile = false,
  index = 0,
  background = "transparent",
  sectionBackgroundColor,
  isLoading = false,
  slotProps,
  bgImage,
  moduleRankingPosition,
  dataModuleName,
  dataParentContainerType,
  children,
  showImageText = false,
  useCircularPlayButton = false,
  ctas = []
}) => {
  if (isLoading || !card) {
    return <FlexibleCardModuleSkeleton />;
  }

  const { overlayText, media, backgroundColor: cardBackgroundColor } = card;

  const cardBackgroundColorToken = cardBackgroundColor
    ? bgColorMap[cardBackgroundColor] || "transparent"
    : "transparent";
  const resolvedBgColor = sectionBackgroundColor
    ? resolveLandingPageSectionBackgroundColor(sectionBackgroundColor)
    : cardBackgroundColorToken;
  const hasVideo = Boolean(media && "videoId" in media ? media.videoId : false);
  let resolvedCtas = ctas;

  if (resolvedCtas.length === 0 && overlayText?.callToActionText) {
    resolvedCtas = [overlayText];
  }

  const videoData: VideoData = {
    id: media && "videoId" in media ? media.videoId : "",
    wistiaVideoId: media && "videoId" in media ? media.videoId : "",
    title: media && "title" in media ? media.title : ""
  };

  const isPrimaryBg = sectionBackgroundColor
    ? isDarkLandingPageBackground(sectionBackgroundColor)
    : resolvedBgColor === "primary.main" || isDarkLandingPageBackground(cardBackgroundColor);
  const anchorTextColor = resolveLandingPageAnchorTextColor(sectionBackgroundColor ?? cardBackgroundColor);

  const textContentProps: MediaTextCardTextComponentProps = {
    headerText: overlayText?.headerText,
    copyText: overlayText?.subtitle || overlayText?.copyText,
    ctas: resolvedCtas,
    anchorTextColor,
    slotProps: {
      header: {
        sx: {
          ...(isPrimaryBg && { color: "primary.contrastText" }),
          ...slotProps?.header?.sx
        }
      },
      copy: {
        sx: {
          ...(isPrimaryBg && { color: "primary.contrastText" }),
          ...slotProps?.copy?.sx
        }
      }
    },
    testId
  };

  const hasModuleData = !!moduleRankingPosition || !!dataModuleName || !!dataParentContainerType;

  return (
    <Box
      data-testid={`media-text-card-section-${testId}`}
      {...(hasModuleData && {
        "data-module-name": dataModuleName || "Media Text Card Section",
        "data-parent-container-type": dataParentContainerType,
        "data-module-ranking-position": moduleRankingPosition,
        "data-ranking-algorithm": "manual",
        "data-track": "visibility",
        component: "section"
      })}
      className="fluid-box"
      sx={deepmerge({
        backgroundColor: resolvedBgColor,
        position: "relative",
        ...(bgImage && {
          "&::before": {
            content: "\"\"",
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${bgImage})`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundSize: "cover",
            opacity: 0.3,
            zIndex: 0,
            pointerEvents: "none"
          }
        })
      }, {
        ...slotProps?.root?.sx
      })}
    >
      <Box
        className="full-width-container"
        sx={deepmerge({
          padding: {
            xs: "4rem 2.4rem",
            md: "4rem 3.2rem ",
            lg: "6rem 10rem"
          }
        }, {
          ...slotProps?.fullWidthContainer?.sx
        })}
        data-testid={`media-text-card-container-${testId}`}
      >
        <Stack
          sx={deepmerge({
            display: "flex",
            flexDirection: {
              desktop: reverseDesktop ? "row-reverse" : "row",
              xs: reverseMobile ? "column-reverse" : "column"
            },
            alignItems: { desktop: "stretch", xs: "initial" },
            justifyContent: { desktop: "center", xs: "initial" },
            backgroundColor: background,
            width: "100%",
            gap: 0
          }, {
            ...slotProps?.container?.sx
          })}
          data-testid={`media-text-card-${testId}`}
        >
          <MediaSection
            hasVideo={hasVideo}
            image={media && "imageUrl" in media ? media : undefined}
            headerText={overlayText?.headerText}
            index={index}
            videoData={videoData}
            showImageText={showImageText}
            slotProps={slotProps?.media ?? undefined}
            useCircularPlayButton={useCircularPlayButton}
          />

          <Box
            sx={deepmerge({
              display: "flex",
              padding: {
                xs: reverseMobile ? "0 0 2.4rem 0" : "2.4rem 0 0 0",
                sm: reverseMobile ? "0 1.8rem 2rem 1.8rem" : "2rem 1.8rem 0 1.8rem",
                md: reverseMobile ? "0 1.8rem 3rem 1.8rem" : "3rem 1.8rem 0 1.8rem",
                lg: reverseDesktop ? "2rem 6rem 2rem 2rem" : "2rem 2rem 2rem 6rem"
              },
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "center",
              gap: { xs: "1.2rem", sm: "2.4rem" },
              flex: "1 0 0"
            }, {
              ...slotProps?.textContainer?.sx
            })}
          >
            {children || <DefaultText {...textContentProps} />}
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default React.memo(MediaTextCard);
